/**
 * Derive WebP variants and responsive metadata for image URLs used on the site.
 * Pexels CDN supports `fm=webp` and width tuning via `w`.
 */

const PEXELS_HOST = /images\.pexels\.com$/i;
const DEFAULT_WIDTHS = [480, 800, 1200];

/**
 * @param {string} src
 * @param {number[]} [widths]
 * @returns {{ webpSrcSet: string | null, fallbackSrc: string, webpAvailable: boolean, sizes: string | undefined }}
 */
export function getOptimizedImageSources(src, widths = DEFAULT_WIDTHS) {
  if (!src || typeof src !== 'string') {
    return { webpSrcSet: null, fallbackSrc: src, webpAvailable: false, sizes: undefined };
  }

  try {
    const originUrl = new URL(src);
    if (!PEXELS_HOST.test(originUrl.hostname)) {
      return { webpSrcSet: null, fallbackSrc: src, webpAvailable: false, sizes: undefined };
    }

    const webpSrcSet = widths
      .map((w) => {
        const u = new URL(src);
        u.searchParams.set('auto', 'compress');
        u.searchParams.set('cs', 'tinysrgb');
        u.searchParams.set('fm', 'webp');
        u.searchParams.set('w', String(w));
        return `${u.toString()} ${w}w`;
      })
      .join(', ');

    const fallback = new URL(src);
    fallback.searchParams.delete('fm');
    fallback.searchParams.set('auto', 'compress');
    fallback.searchParams.set('cs', 'tinysrgb');
    if (!fallback.searchParams.get('w')) fallback.searchParams.set('w', '1200');

    return {
      webpSrcSet,
      fallbackSrc: fallback.toString(),
      webpAvailable: true,
      sizes: '(max-width: 768px) 100vw, (max-width: 1200px) 70vw, 800px',
    };
  } catch {
    return { webpSrcSet: null, fallbackSrc: src, webpAvailable: false, sizes: undefined };
  }
}

/**
 * Same-origin public assets: companion `.webp` next to `.png`/`.jpeg` in /public.
 * @param {string} src - e.g. `${PUBLIC_URL}/black.png` or absolute URL to this site
 * @returns {string | null} path like `/black.webp` for use in srcSet
 */
export function getPublicWebpCandidate(src) {
  if (!src || typeof src !== 'string') return null;

  try {
    const pub = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
    let pathPart = src;

    if (/^https?:\/\//i.test(src)) {
      const pathUrl = new URL(src);
      pathPart = pathUrl.pathname + pathUrl.search;
    }

    if (pub && pathPart.startsWith(`${pub}/`)) {
      pathPart = pathPart.slice(pub.length);
    }

    if (!pathPart.startsWith('/')) return null;
    if (!/\.(png|jpe?g)$/i.test(pathPart)) return null;

    return pathPart.replace(/\.(png|jpe?g)$/i, '.webp');
  } catch {
    return null;
  }
}
